import { useState, useRef, useEffect, useCallback } from 'react';

import { useUser, useClerk } from '@clerk/clerk-react';
import { getInitials, type OpenPanel } from '../components/V2Nav';
import { useIsMobile } from '../hooks/useIsMobile';
import { usePrimateAuth } from '../hooks/usePrimateAuth';
import { useVideoLibrary } from '../hooks/useVideoLibrary';
import { useFileUpload } from '../hooks/useFileUpload';
import { useInference } from '../hooks/useInference';
import { useAnalysis } from '../hooks/useAnalysis';
import { useStreamSession } from '../hooks/useStreamSession';
import { usePrompts } from '../hooks/usePrompts';

import type { NarrativeEntry } from '../lib/narrative';
import { useTestEscapeHatches } from '../hooks/useTestEscapeHatches';
import { useAuthToken } from '../hooks/useAuthToken';
import { useVideoHandlers } from '../hooks/useVideoHandlers';
import { useSessionRestore } from '../hooks/useSessionRestore';
import { useFlyAnimation } from '../primate/hooks/useFlyAnimation';
import { useVisualViewportKeyboard } from '../primate/hooks/useVisualViewportKeyboard';
import { useResultPillHandlers } from '../hooks/useResultPillHandlers';
import { useSubmitHandlers } from '../hooks/useSubmitHandlers';
import { useStreamLifecycle } from '../primate/hooks/useStreamLifecycle';
import { V2Providers } from '../components/v2/V2Providers';
import { type ResultPill, type VideoSource, stripCompiledQueryEnvelope } from '../components/v2/pv2-shared';
import { useQuotaBar } from '../primate/hooks/useQuotaBar';
import { getStoredAnonKey, ANON_KEY_CHANGED_EVENT } from '../core/lib/api/anon-key';
import { AnonTrialExhausted } from '../components/v2/AnonTrialExhausted';
import { AnonProvisioningFailed } from '../components/v2/AnonProvisioningFailed';
import { AnonInfoBanner } from '../components/v2/AnonInfoBanner';
import { useCostPreview, getVideoFileDuration } from '../primate/hooks/useCostPreview';
import { useQueryParser } from '../core/hooks/useQueryParser';
import type { CompiledQuery, ParseState } from '../core/components/v2/compiled-query-types';
import { WaitlistGate } from '../primate/gates/WaitlistGate';
import { DesktopLayout } from '../components/v2/DesktopLayout';
import { MobileLayout } from '../components/v2/MobileLayout';
import TermsModal from '../components/TermsModal';
import { useTermsConsent } from '../primate/hooks/useTermsConsent';
import { trackSourceModeChanged } from '../lib/analytics';

function PrimateVisionV2Inner() {
  // Force light mode for this page
  useEffect(() => {
    const prev = document.documentElement.getAttribute('data-theme');
    document.documentElement.setAttribute('data-theme', 'light');
    return () => { document.documentElement.setAttribute('data-theme', prev ?? 'dark'); };
  }, []);

  // ── Auth ───────────────────────────────────────────────────────────────────
  const {
    isSignedIn, isPro, authLoaded, isAdmin, isRestricted,
    getToken,
    demoUsageCount, setDemoUsageCount,
  } = usePrimateAuth();
  const { fetchToken } = useAuthToken();

  // ── First-run Terms & Conditions consent gate ─────────────────────────────
  // Under the dev/test bypass (primate-dev-signed-in) we skip the server fetch
  // and the mount-time gate so the existing per-action TermsModal e2e coverage
  // (and the ~100 specs that pre-seed the localStorage key) keep exercising the
  // submit-time path. Real Clerk sessions always get the first-run gate.
  const termsDevBypass = typeof window !== 'undefined'
    && window.localStorage.getItem('primate-dev-signed-in') === '1';
  const termsForceGate = typeof window !== 'undefined'
    && window.localStorage.getItem('primate-dev-firstrun-gate') === '1';
  // PRI-503 (waitlist removal, anon free trial): anonymous visitors now land
  // directly in the product — the full-screen mount-time Terms wall would block
  // the "try it immediately" landing, so anon consent moves to the PER-ACTION
  // gate (requireTermsAccepted in useSubmitHandlers fires before the first
  // stream/upload — consent still strictly precedes any processing, and the
  // server-side enforcement on stream WS / upload remains the backstop).
  // The 2026-06-11 fail-closed rule (mount-gate every anon user) was designed
  // for the invited-anon waitlist era; the per-action gate preserves its
  // guarantee — no anon prompt is ever processed without consent — without
  // walling off the landing. Signed-in users keep the first-run mount gate.
  const { accepted: termsAccepted, gateActive: termsGateActive, markAccepted: markTermsAccepted } = useTermsConsent({
    isSignedIn: !!isSignedIn,
    isAdmin: !!isAdmin,
    authLoaded: !!authLoaded,
    devBypass: termsDevBypass,
    forceGateUnderBypass: termsForceGate,
    gateAnonInvited: false,
  });

  // ── User display info (for shared sidebar) ─────────────────────────────────
  // useUser is safe here - PrimateVisionV2Inner is always rendered inside ClerkProvider.
  // Falls back gracefully when Clerk isn't loaded yet.
  const { user: clerkUser } = useUser();
  const { signOut } = useClerk();
  const rawFirstName = clerkUser?.firstName ?? '';
  const rawLastName = clerkUser?.lastName ?? '';
  const hasSpaceInFirstName = rawFirstName.trim().includes(' ');
  const fullName = clerkUser
    ? (hasSpaceInFirstName ? rawFirstName.trim() : [rawFirstName, rawLastName].filter(Boolean).join(' ').trim() || clerkUser.username || null)
    : null;
  const userInitials = getInitials(fullName);
  const userName = fullName || null;
  const planTier = (clerkUser?.publicMetadata as { plan?: string } | undefined)?.plan ?? 'free';
  const [canDownloadOverride, setCanDownloadOverride] = useState<boolean | null>(null);
  const canDownload = canDownloadOverride !== null ? canDownloadOverride : planTier !== 'free';
  const handleSignOut = () => signOut({ redirectUrl: '/' });

  const {
    demoVideos,
    userVideosLoaded,
    savedUserVideos, setSavedUserVideos,
    selectedSavedVideo, setSelectedSavedVideo,
    videoQueue, setVideoQueue,
    activeVideoId, setActiveVideoId,
    DEMO_LIMIT,
  } = useVideoLibrary({ isSignedIn, authLoaded, isAdmin, demoUsageCount });

  const streamUserVideos = savedUserVideos.filter(v => v.display_name.startsWith('Live: ') || v.filename === 'live-stream');
  const regularUserVideos = savedUserVideos.filter(v => !v.display_name.startsWith('Live: ') && v.filename !== 'live-stream');

  const {
    selectedFile, setSelectedFile,
    selectedDemoVideo, setSelectedDemoVideo,
    conversionFile, setConversionFile,
    showConversionDialog, setShowConversionDialog,
    setFileError,
    uploadProgress,
    setUploadProgress,
    uploadPath, setUploadPath,
  } = useFileUpload();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarPanel, setSidebarPanel] = useState<OpenPanel>(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const [prompt, setPrompt] = useState(() => {
      try {
      const saved = sessionStorage.getItem('primate-pending-prompt');
      if (saved) { sessionStorage.removeItem('primate-pending-prompt'); return saved; }
    } catch {}
    return '';
  });
  // Compiled query — useQueryParser debounces NL→JSON, result threaded to InputBar
  const { query: parsedQuery, parsing: queryParsing, error: queryError } = useQueryParser(prompt);
  const [editMode, setEditMode] = useState(false);
  const [overriddenQuery, setOverriddenQuery] = useState<CompiledQuery | null>(null);
  // User-edited query overrides the auto-parsed one; cleared when prompt changes
  const compiledQuery = overriddenQuery ?? parsedQuery;
  const parseState: ParseState = (() => {
    if (!prompt.trim()) return 'idle';
    if (editMode) return 'edit';
    if (queryParsing) return 'parsing';
    if (queryError) return 'error';
    if (compiledQuery) return 'lanes_visible';
    return 'idle';
  })();
  // When prompt changes, clear any user-edited override so fresh NL parse takes over
  useEffect(() => { setOverriddenQuery(null); }, [prompt]);

  // PRI-437 P8: user-facing quality selection removed — one metered rate.
  const [processOffline, setProcessOffline] = useState(false);
  // PRI-308: SAM behavior is prompt-driven server-side; UI no longer exposes a toggle.
  const useSamForRequests = true;
  const [videoMenuOpen, setVideoMenuOpen] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [promptSent, setPromptSent] = useState(false);
  const [streamProgressDismissing, setStreamProgressDismissing] = useState(false);
  const [offlineProgressDismissing, setOfflineProgressDismissing] = useState(false);
  const [streamErrorState, setStreamErrorState] = useState<'idle' | 'error' | 'retrying'>('idle');
  const autoRetryCountRef = useRef(0);
  const streamWasLiveRef = useRef(false);
  const autoRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [recordingLimitBannerShown, setRecordingLimitBannerShown] = useState(false);
  const [frozenFrameUrl, setFrozenFrameUrl] = useState<string | null>(null);
  const [quotaRefreshKey, setQuotaRefreshKey] = useState(0);
  const [sourceMode, setSourceModeRaw] = useState<'webcam' | 'file'>('webcam');
  // First-use UX (Matt 2026-07-01): don't RENDER Webcam as pre-selected before
  // camera permission is granted. A blue "Webcam" pill on first visit reads as
  // already-active, so users don't realize they must click it to trigger the
  // browser permission prompt. This is presentation-only: the internal
  // sourceMode state machine (pre-connect gate, videoSource derivation,
  // carousel visibility) is untouched. displaySourceMode is null until the
  // user explicitly picks a source OR permission is already granted
  // (returning user → webcam lights up as before).
  const [sourceChosen, setSourceChosen] = useState(false);
  const setSourceMode = useCallback((m: 'webcam' | 'file' | null) => {
    if (m === null) { setSourceChosen(false); return; }
    setSourceChosen(true);
    setSourceModeRaw(prev => {
      // Only fire analytics when the mode actually changes (skip same-value calls)
      if (prev !== m) trackSourceModeChanged({ mode: m, user_plan: planTier });
      return m;
    });
  }, [planTier]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    let cancelled = false;
    try {
      if (!navigator.permissions?.query) return; // can't know → require explicit choice
      navigator.permissions.query({ name: 'camera' as PermissionName })
        .then(st => { if (!cancelled && st.state === 'granted') setSourceChosen(true); })
        .catch(() => { /* browser doesn't support 'camera' query → stay unselected */ });
    } catch { /* Permissions API unavailable */ }
    return () => { cancelled = true; };
  }, []);
  const displaySourceMode: 'webcam' | 'file' | null = sourceChosen ? sourceMode : null;
  const [resultPills, setResultPills] = useState<ResultPill[]>([]);
  const [anyPillApiOpen, setAnyPillApiOpen] = useState(false);
  const [downloadingPillIds, setDownloadingPillIds] = useState<Set<string>>(new Set());
  const [showUpgradeForDownload, setShowUpgradeForDownload] = useState(false);
  const [activePrompt, setActivePrompt] = useState('');
  const activePromptRef = useRef(activePrompt);
  activePromptRef.current = activePrompt;
  const isSignedInRef = useRef(!!isSignedIn);
  isSignedInRef.current = !!isSignedIn;
  const [demoVideoUserVideoIds, setDemoVideoUserVideoIds] = useState<Record<string, string>>({});
  const submittedThumbRef = useRef<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const inputBarContainerRef = useRef<HTMLDivElement>(null);
  const threadAreaRef = useRef<HTMLDivElement>(null);

  const {
    afterState, setAfterState, beforeStateActive, setBeforeStateActive,
    carouselVisible, setCarouselVisible, carouselDismissed, setCarouselDismissed,
    slotRevealed, setSlotRevealed, camRef, flyDivRef,
    capturedCamElRef, camRectRef, flyThumbnailRef, threadBottomRef, desktopThreadAreaRef,
    videoSlotRefCallback,
  } = useFlyAnimation({ resultPills });

  useTestEscapeHatches({ __pv2_setAfterState: setAfterState, __pv2_setCarouselVisible: setCarouselVisible, __pv2_setCarouselDismissed: setCarouselDismissed, __pv2_setResultPills: setResultPills, __pv2_setCanDownloadOverride: setCanDownloadOverride, __pv2_setIsStreaming: setIsStreaming, __pv2_setConversionFile: setConversionFile, __pv2_setShowConversionDialog: setShowConversionDialog, __pv2_setSelectedFile: setSelectedFile, __pv2_setSourceMode: setSourceMode });

  const isMobile = useIsMobile();
  const [offlineJobActive, setOfflineJobActive] = useState(false);
  const promptTextareaRef = useRef<HTMLTextAreaElement>(null);

  // Onboarding v3 (2026-05-25): camera permission must wait until the user
  // explicitly chooses Webcam via VideoSourcePopover ("Try it for yourself"
  // → Webcam). Auto pre-warming on mount surfaced the browser's camera
  // permission dialog over the EmptyStateDemo before any user interaction.
  // The on-demand getUserMedia call still lives in DesktopLayout /
  // MobileLayout / useStreamLifecycle when the user picks webcam.

  const vibrate = useCallback((pattern: number | number[]) => {
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator && isMobile) {
      navigator.vibrate(pattern);
    }
  }, [isMobile]);

  useVisualViewportKeyboard(inputBarContainerRef, threadAreaRef, isMobile);

  const {
    pageState, pageStateRef, setPageStateSync,
    jobId, setJobId,
    jobProgress, setJobProgress,
    resultCacheRef, analysisGenRef,
    displayedJobId, setDisplayedJobId,
    displayedResult, setDisplayedResult,
    videoKeyRef,
    setResult,
    setAnalysisErrorMessage,
    setQueuePosition,
    processingStep, setProcessingStep,
    completingSteps,
    setRevealPhase,
    setIsDemoResult,
    startPolling, stopPolling,
    timeEstimate,
  } = useInference({
    isSignedIn, isPro,
    activeVideoId,
    setVideoQueue,
    setUploadProgress,
    uploadPath,
    useSam: useSamForRequests,
  });

  const { prompts, setSelectedPrompt } = usePrompts();

  const streamSession = useStreamSession();

  useEffect(() => {
    // Disabled 2026-07-01 after prod outage: the pre-connect path creates a
    // sidecar session with prompt="__PENDING__" and relies on a browser data
    // channel `initial_prompt` message to start inference. Prod logs showed
    // WebRTC connected + frames decoded, but `initial_prompt` never arrived, so
    // inference never started and users saw a black "RE-RUN STREAM" card. The
    // normal submit path sends the prompt in the initial join/offer and is the
    // correctness path; keep pre-connect off until it has a server-side fallback.
    const PRECONNECT_ENABLED = false;
    if (!PRECONNECT_ENABLED) return;
    // Gate the pre-connect WS on consent. Opening it before terms are accepted
    // means the server rejects it with terms_not_accepted at auth and the
    // rejection is swallowed by the pre-connect (not-yet-active) error path,
    // producing a silent black "RE-RUN STREAM" on the first post-consent submit
    // (PRI consent-firstprompt race, 2026-06-26). termsAccepted === null means
    // "still resolving" — don't pre-connect until it's definitively true.
    if (isStreaming || sourceMode !== 'webcam' || streamSession.cameraPermission !== 'granted' || streamSession.connectionState !== 'idle' || !getToken || termsAccepted !== true) return;
    void getToken().then(t => { if (!isStreaming && streamSession.connectionState === 'idle') void streamSession.preConnect(t); }).catch(() => {});
  }, [isStreaming, sourceMode, streamSession.cameraPermission, streamSession.connectionState, termsAccepted]); // eslint-disable-line react-hooks/exhaustive-deps

  // PRI-503: anonymous free-trial state — tracks whether an anon pv_live_ key
  // is stored (enables the quota bar for signed-out users) and whether the
  // trial is exhausted (sandbox/upgrade IP cap → friendly signup upsell).
  const [anonKeyPresent, setAnonKeyPresent] = useState(() => getStoredAnonKey() !== null);
  const [anonTrialExhausted, setAnonTrialExhausted] = useState(false);
  // 2026-08-25 fix: any anon-key provisioning failure OTHER than trial
  // exhaustion (network blip, transient error, cold start) now surfaces this
  // friendly retry prompt instead of silently firing a doomed unauthenticated
  // request that the server rejects with a raw "Authentication failed" error
  // deep in the product UI. See /tmp/plan-login-auth-guard-2026-08-25.md.
  const [anonProvisioningFailed, setAnonProvisioningFailed] = useState(false);
  useEffect(() => {
    const sync = () => setAnonKeyPresent(getStoredAnonKey() !== null);
    window.addEventListener(ANON_KEY_CHANGED_EVENT, sync);
    return () => window.removeEventListener(ANON_KEY_CHANGED_EVENT, sync);
  }, []);

  const rawQuota = useQuotaBar(!!isSignedIn, quotaRefreshKey, !isSignedIn && anonKeyPresent);
  const quotaBar = (!isAdmin && rawQuota !== null && (rawQuota.loading || rawQuota.balanceSeconds !== null))
    ? rawQuota
    : null;

  const videoQueueRef = useRef<typeof videoQueue>([]); useEffect(() => { videoQueueRef.current = videoQueue; }, [videoQueue]);

  // ── useAnalysis (main handler hook) ───────────────────────────────────────
  const {
    handleFileSelected,
    handleConversionConfirm,
    handleConversionCancel,
    handleAnalyseWithPrompt: _handleAnalyseWithPrompt,
  } = useAnalysis({
    isSignedIn, isPro, getToken,
    setSelectedFile, setSelectedDemoVideo,
    setConversionFile, setShowConversionDialog,
    setFileError, setUploadProgress, setUploadPath,
    videoQueue, setVideoQueue, activeVideoId, setActiveVideoId,
    setSavedUserVideos, setSelectedSavedVideo,
    selectedFile, selectedDemoVideo, selectedSavedVideo,
    demoVideos, demoUsageCount, setDemoUsageCount, DEMO_LIMIT,
    pageStateRef, setPageStateSync,
    setJobId, jobId,
    setResult, setDisplayedResult, setDisplayedJobId,
    setAnalysisErrorMessage, setQueuePosition, setJobProgress,
    setRevealPhase, setIsDemoResult, setProcessingStep,
    resultCacheRef, analysisGenRef, videoKeyRef,
    startPolling, stopPolling,
    videoQueueRef,
    prompts,
    setSelectedPrompt,
    setResultTab: () => {}, // V2 has no result tabs
    useSam: useSamForRequests,
  });

  // Wrap handleAnalyseWithPrompt to track activePrompt for pill display.
  // Callers (useSubmitHandlers offline-batch + file-live-fallback paths)
  // pass the wire-format prompt with the |||COMPILED_QUERY:<base64>|||
  // envelope so the inference API still receives the structured-query
  // override. activePrompt is a UI surface (renders in pill heading,
  // mobile layout, JSON tray) so strip the envelope here before storing.
  // The wire format goes to _handleAnalyseWithPrompt unchanged.
  const handleAnalyseWithPrompt = (p: string) => {
    setActivePrompt(stripCompiledQueryEnvelope(p));
    return _handleAnalyseWithPrompt(p);
  };

  useSessionRestore({
    isSignedIn,
    authLoaded,
    userVideosLoaded,
    pageState,
    pageStateRef,
    setJobId,
    setDisplayedJobId,
    videoKeyRef,
    setActivePrompt,
    setSelectedPrompt,
    setQueuePosition,
    setPageStateSync,
    startPolling,
  });

  // ── Derived: videoSource from real hook state ──────────────────────────────
  const activeEntry = videoQueue.find(v => v.id === activeVideoId);
  // For file streaming: keep file info visible, use 'stream' kind to trigger preview
  // isFileStream from streamSession indicates we're streaming from a file
  const videoSource: VideoSource | null = isStreaming
    ? streamSession.isFileStream
      // File streaming: show file name but use 'stream' kind to show live preview
      ? selectedFile
        ? { kind: 'stream', name: selectedFile.name, thumbUrl: activeEntry?.thumbnailUrl || undefined }
        : selectedDemoVideo
        ? (() => {
            const dv = demoVideos.find(v => v.id === selectedDemoVideo);
            return dv ? { kind: 'stream' as const, name: dv.display_name, thumbUrl: dv.thumbnail_url ?? undefined } : null;
          })()
        : { kind: 'stream', name: 'Video File', thumbUrl: undefined }
      // Camera streaming: show 'Live Camera'
      : { kind: 'stream', name: 'Live Camera', thumbUrl: undefined }
    // Regression fix (Arian 2026-06-22): when NOT streaming, a selected webcam
    // source must PERSIST across prompts. Previously this fell through to null the
    // moment isStreaming went false, so the camera chip vanished after every
    // prompt and the user had to re-select the camera each time. If we're in
    // webcam mode with a usable camera (live stream held OR permission granted)
    // and no file/demo/saved source is chosen, keep showing the Live Camera chip.
    : (sourceMode === 'webcam'
        && !selectedFile && !selectedDemoVideo && !selectedSavedVideo
        && (streamSession.localStream != null || streamSession.cameraPermission === 'granted'))
    ? { kind: 'stream' as const, name: 'Live Camera', thumbUrl: undefined }
    : selectedFile
    ? { kind: 'file', name: selectedFile.name, thumbUrl: activeEntry?.thumbnailUrl || undefined }
    : selectedDemoVideo
    ? (() => {
        const dv = demoVideos.find(v => v.id === selectedDemoVideo);
        return dv ? { kind: 'file' as const, name: dv.display_name, thumbUrl: dv.thumbnail_url ?? undefined } : null;
      })()
    : selectedSavedVideo
    ? { kind: 'file' as const, name: selectedSavedVideo.display_name, thumbUrl: selectedSavedVideo.thumbnail_data_url ?? undefined }
    : null;

  // ── Cost preview (PRI-488 F1: validate_only dry-run before Run) ──────────
  // Only for the file-analysis path (uploads / saved library videos) —
  // streaming is metered per live second and has its own countdown (F4).
  const [fileDurationS, setFileDurationS] = useState<number | null>(null);
  useEffect(() => {
    let cancelled = false;
    if (selectedFile) {
      void getVideoFileDuration(selectedFile).then(d => { if (!cancelled) setFileDurationS(d); });
    } else {
      setFileDurationS(null);
    }
    return () => { cancelled = true; };
  }, [selectedFile]);
  const costPreview = useCostPreview({
    enabled: !!isSignedIn && !isStreaming && videoSource?.kind === 'file'
      && (pageState === 'idle' || pageState === 'complete'),
    compiledQuery,
    publicVideoId: activeEntry?.publicVideoId ?? null,
    durationS: selectedFile
      ? fileDurationS
      : (selectedSavedVideo?.duration_seconds ?? null),
    balanceSeconds: quotaBar?.balanceSeconds ?? null,
  });

  // ── processingText ─────────────────────────────────────────────────────────
  const processingText = pageState === 'processing'
    ? (jobProgress?.message ?? (timeEstimate <= 5 ? 'Almost done' : `Analysing · ~${timeEstimate}s`))
    : uploadPath === 'uploading'
    ? `Uploading · ${((selectedFile?.size ?? 0) / 1024 / 1024).toFixed(1)}MB`
    : uploadPath === 'checking'
    ? 'Checking results...'
    : 'Working...';

  const lastStreamResultRef = useRef<typeof streamSession.inferenceResult>(null);
  const streamPillIdRef = useRef<string | null>(null);
  // Test escape hatch: lets E2E specs designate which injected pill is the
  // active live-stream pill (drives isLiveStreamActive → Zone-2 pinned render
  // and in-pill video-slot collapse). streamPillIdRef is a ref (no re-render on
  // its own); tests call __pv2_setIsStreaming(true) immediately after to commit
  // a render that recomputes isLiveStreamActive. Registered after the ref exists
  // (the primary useTestEscapeHatches call near the top runs before this line).
  useTestEscapeHatches({ __pv2_setStreamPillId: (id: string | null) => { streamPillIdRef.current = id; } });
  // Preserved for one render after streamPillIdRef is cleared (frozenFrame effect reads it)
  const lastStoppedPillIdRef = useRef<string | null>(null);
  const uploadingPillIdsRef = useRef<Set<string>>(new Set());
  const streamHistoryRef = useRef<Array<{ frame_num: number; answer: string; confidence: number; elapsed_s?: number; term_confidences?: Record<string, number>; recording_elapsed_ms?: number }>>([]);
  const narrativeRef = useRef<NarrativeEntry[]>([]);

  // ── Stream lifecycle effects (extracted to useStreamLifecycle — Phase 5 PRI-291) ──
  // ── Result pill handlers (extracted to useResultPillHandlers — Phase 5 Step 5) ──
  // ── Result pill handlers (extracted to useResultPillHandlers — Phase 5 Step 5) ──
  const {
    attachRenderedVideo,
    attachRenderedVideoForced,
    attachStreamRecordingUrl,
    attachStreamRecordingUrlForced,
    handleRerunStream,
    handleDownloadPill,
    handleDeleteResult,
  } = useResultPillHandlers({
    fetchToken,
    setResultPills,
    canDownload,
    setShowUpgradeForDownload,
    planTier,
    setDownloadingPillIds,
    setPrompt,
    setIsStreaming,
    lastStreamResultRef,
    requestCameraAccess: streamSession.requestCameraAccess,
  });

  useStreamLifecycle({
    // State from context (passed explicitly since hook runs outside V2Providers)
    streamSession, isStreaming, setIsStreaming, resultPills, setResultPills,
    activePrompt, setFrozenFrameUrl, frozenFrameUrl,
    pageState, displayedResult: displayedResult as Record<string, unknown> | null, jobId, displayedJobId,
    demoVideos: demoVideos as { id: string; display_name: string; [k: string]: unknown }[], 
    selectedSavedVideo: selectedSavedVideo as { id: string; [k: string]: unknown } | null, selectedDemoVideo, activeVideoId,
    isSignedIn, isPro,
    // Refs
    streamPillIdRef, lastStoppedPillIdRef, uploadingPillIdsRef, streamHistoryRef, narrativeRef,
    lastStreamResultRef, isSignedInRef, activePromptRef,
    autoRetryCountRef, autoRetryTimerRef, streamWasLiveRef, submittedThumbRef,
    videoQueueRef: videoQueueRef as unknown as import('react').RefObject<{ id: string; userVideoId?: string; [key: string]: unknown }[]>,
    setStreamErrorState, setStreamProgressDismissing, setOfflineProgressDismissing,
    offlineJobActive, setOfflineJobActive, recordingLimitBannerShown, setRecordingLimitBannerShown,
    setQuotaRefreshKey, setSlotRevealed, setPrompt, setSavedUserVideos,
    setAfterState, setBeforeStateActive,
    demoVideoUserVideoIds, setDemoVideoUserVideoIds,
    vibrate, attachStreamRecordingUrl, attachRenderedVideo,
  });

  const {
    handleSubmit,
    handleTermsAccepted: handleTermsAcceptedForAction,
    showTermsForAction,
  } = useSubmitHandlers({
    prompt,
    videoSource,
    processOffline,
    isPro,
    samEnabled: useSamForRequests,
    selectedFile,
    selectedDemoVideo,
    selectedSavedVideo,
    demoVideos,
    setIsStreaming,
    setStreamErrorState,
    setActivePrompt,
    setPromptSent,
    setResultPills,
    setAfterState,
    setCarouselVisible,
    setOfflineJobActive,
    submittedThumbRef,
    streamPillIdRef,
    streamHistoryRef,
    narrativeRef,
    flyThumbnailRef,
    capturedCamElRef,
    camRef,
    camRectRef,
    getToken,
    isSignedIn: !!isSignedIn,
    onAnonTrialExhausted: () => setAnonTrialExhausted(true),
    onAnonProvisioningFailed: () => setAnonProvisioningFailed(true),
    streamSession,
    handleAnalyseWithPrompt,
    compiledQuery,
  });

  // Per-action accept must ALSO persist consent server-side (and mirror it into
  // the first-run gate state) so the server enforcement (stream WS / upload)
  // doesn't reject the very action the user just unblocked. markTermsAccepted
  // writes localStorage + PATCHes /v1/user/profile; handleTermsAcceptedForAction
  // re-runs the stashed submit callback.
  const handleTermsAccepted = useCallback(async () => {
    // AWAIT the server-side consent write before re-running the stashed submit.
    // markTermsAccepted() PATCHes /v1/user/profile; the stream WS auth gate reads
    // terms_accepted_at fresh from the DB, so re-running the submit before the
    // PATCH commits makes the server silently reject the first stream (the very
    // action the user just unblocked). Awaiting closes the read-your-own-write
    // race at its source (PRI consent-firstprompt bug, 2026-06-26).
    await markTermsAccepted();
    handleTermsAcceptedForAction();
  }, [markTermsAccepted, handleTermsAcceptedForAction]);

  const {
    handleSelectUserVideo,
    handleDeleteUserVideo,
    handleDeleteDemoVideoSession,
    handleDeleteLiveStreamSessions,
    handleSelectDemoVideo,
    handleSelectLiveStreams,
    handleLiveStreamsNewSession,
  } = useVideoHandlers({
    fetchToken,
    savedUserVideos,
    demoVideos,
    streamUserVideos,
    setSavedUserVideos,
    setSelectedSavedVideo,
    setSelectedDemoVideo,
    setSelectedFile,
    selectedSavedVideo,
    selectedDemoVideo,
    demoVideoUserVideoIds,
    setDemoVideoUserVideoIds,
    setSidebarOpen,
    setResultPills,
    setIsStreaming,
    requestCameraAccess: streamSession.requestCameraAccess,
    lastStreamResultRef,
    attachRenderedVideo,
    attachStreamRecordingUrl,
    setPageStateSync,
    prompt,
    userPlan: planTier,
    isAuthenticated: !!isSignedIn,
  });

  const streamCs = streamSession.connectionState;
  const isStreamingActive = isStreaming && !['idle','error','stopped','camera_denied','no_camera','queue_full'].includes(streamCs);
  // Onboarding coach (2026-05-25): when the signed-in user has persistently
  // dismissed the coach via the in-coach "Don't show again" link or via the
  // settings re-enable section, suppress the entire onboarding surface. The
  // flag is only ever set behind signed-in surfaces, so anonymous users can
  // never persist a dismissal — they always see the demo + coach.
  const [onboardingCoachDismissed, setOnboardingCoachDismissed] = useState(
    () => typeof window !== 'undefined' && window.localStorage.getItem('pv2-onboarding-coach-dismissed') === 'true'
  );
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === 'pv2-onboarding-coach-dismissed') {
        setOnboardingCoachDismissed(e.newValue === 'true');
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  // Phase 2 (2026-05-23): Demo Reel as the workspace empty state. Render only
  // when nothing the user could already be inspecting is on screen — i.e. no
  // selected file/demo/stream source, no pending inference, no result pills.
  const showCarousel = authLoaded
    && resultPills.length === 0
    && pageState === 'idle'
    && !isStreamingActive
    && !videoSource
    && !selectedFile
    && !selectedDemoVideo
    && !onboardingCoachDismissed;
  const inputCompact = isMobile && resultPills.length > 0 && pageState === 'idle' && !isStreaming && !isStreamingActive;
  const showProgressBase = pageState === 'processing' || pageState === 'uploading';
  const showProgress = showProgressBase || offlineProgressDismissing;
  // Show stream setup tray until the first annotated frame arrives (connectionState='live').
  const isWebcamFlyStream = isStreaming && sourceMode === 'webcam';
  const [forceStreamProgress, setForceStreamProgress] = useState(false);
  useTestEscapeHatches({ __pv2_forceStreamProgress: setForceStreamProgress });
  const showStreamProgress = forceStreamProgress || (!isWebcamFlyStream && !beforeStateActive && isStreamingActive && pageState === 'idle' && streamCs !== 'recalculating' && (
    ['connecting', 'queued', 'buffering'].includes(streamCs) || streamProgressDismissing
  ));

  const [mobileHUDOpen, setMobileHUDOpen] = useState(false);
  useEffect(() => { if (!isStreaming) setMobileHUDOpen(false); }, [isStreaming]);
  // Mobile fullscreen HUD disabled 2026-05-27 (Matt — needs more design thought).
  // Re-enable by setting MOBILE_HUD_ENABLED=true. Component (MobileStreamingHUD) and
  // its tests remain in tree so we can iterate.
  const MOBILE_HUD_ENABLED = false;
  const showMobileHUD = MOBILE_HUD_ENABLED && isMobile && mobileHUDOpen && isStreaming && [
    'connecting', 'buffering', 'live', 'degraded',
    'quota_warning', 'recalculating', 'stopping'
  ].includes(streamSession.connectionState);

  if (authLoaded && isSignedIn && isRestricted) {
    return <WaitlistGate onSignOut={handleSignOut} />;
  }

  // First-run consent gate: a signed-in, non-admin user who has not accepted the
  // Terms & Conditions sees ONLY the full-screen TermsModal — the interactive
  // app (camera picker, prompt input bar, HUD) is not rendered until they accept.
  // This is the core mobile-bug fix: instead of a per-action modal that rendered
  // under the tabbar/HUD/input bar, nothing interactive exists to render under.
  // The TermsModal overlay itself sits at z=10000 (Item 2) for defense-in-depth.
  if (termsGateActive) {
    return <TermsModal onAccept={markTermsAccepted} />;
  }

  // Context values for V2Providers
  const inferenceContextValue = { pageState, setPageStateSync, jobId: jobId ?? null, displayedJobId: displayedJobId ?? null, displayedResult: displayedResult as Record<string, unknown> | null ?? null, analysisErrorMessage: null, startAnalysis: handleAnalyseWithPrompt, cancelAnalysis: () => { stopPolling(); setPageStateSync('idle'); }, resetToIdle: () => setPageStateSync('idle') };
  const streamContextValue = { ...streamSession, isStreaming, setIsStreaming, promptSent, setPromptSent, resultPills, setResultPills, samEnabled: false, setSamEnabled: (_v: boolean) => {}, processOffline, setProcessOffline, sourceMode: displaySourceMode, setSourceMode, frozenFrameUrl, setFrozenFrameUrl, activePrompt, setActivePrompt };
  const clearVideoSelection = () => { setSelectedFile(null); setSelectedDemoVideo(null); setSelectedSavedVideo(null); };
  const videoLibraryContextValue = { savedUserVideos, demoVideos, regularUserVideos, streamUserVideos, videoQueue, activeVideoId: activeVideoId ?? null, uploadProgress, selectedFile: selectedFile ?? null, selectedDemoVideo: selectedDemoVideo ?? null, selectedSavedVideo: selectedSavedVideo ?? null, selectFile: (f: File | null) => setSelectedFile(f), selectDemoVideo: (v: string | null) => setSelectedDemoVideo(v), selectSavedVideo: (v: typeof selectedSavedVideo | null) => setSelectedSavedVideo(v), clearSelection: clearVideoSelection, deleteVideo: handleDeleteUserVideo };

  // PRI-503: anon free-trial exhaustion overlay — rendered over either layout.
  const anonTrialOverlay = anonTrialExhausted && !isSignedIn
    ? <AnonTrialExhausted onDismiss={() => setAnonTrialExhausted(false)} />
    : null;

  // 2026-08-25 fix: friendly retry prompt for non-exhaustion anon-key
  // provisioning failures (see plan doc above) — rendered over either layout,
  // same pattern as anonTrialOverlay.
  const anonProvisioningFailedOverlay = anonProvisioningFailed && !isSignedIn
    ? (
      <AnonProvisioningFailed
        onRetry={() => setAnonProvisioningFailed(false)}
        onDismiss={() => setAnonProvisioningFailed(false)}
      />
    )
    : null;

  // PRI-503: signed-out app.* gets a slim "what is this" banner linking back
  // to the marketing site (apex). Render only once auth state is resolved so
  // signed-in users never see a flash of the anon banner.
  const anonInfoBanner = authLoaded && !isSignedIn ? <AnonInfoBanner /> : null;

  if (isMobile) return (
    <V2Providers values={{ inference: inferenceContextValue, stream: streamContextValue, videoLibrary: videoLibraryContextValue }}>
      {anonInfoBanner}
      {anonTrialOverlay}
      {anonProvisioningFailedOverlay}
      <MobileLayout {...{
        showTermsForAction, handleTermsAccepted, showConversionDialog, conversionFile,
        handleConversionConfirm, handleConversionCancel, showUpgradeForDownload, setShowUpgradeForDownload,
        showMobileHUD, moreOpen, setMoreOpen, setMobileHUDOpen,
        showCarousel, authLoaded, carouselDismissed, setCarouselDismissed,
        showProgress, showStreamProgress, streamProgressDismissing, offlineProgressDismissing,
        processingText, processingStep, jobProgress, completingSteps, offlineJobActive,
        prompt, setPrompt, videoMenuOpen, setVideoMenuOpen, videoSource, quotaBar, costPreview,
        publicVideoId: activeEntry?.publicVideoId ?? null,
        compiledQuery, parseState, queryError,
        onCompiledQueryChange: setOverriddenQuery,
        onEditModeToggle: setEditMode,
        canDownload, downloadingPillIds, inputCompact, streamPillIdRef, uploadingPillIdsRef,
        threadAreaRef, threadBottomRef, inputBarContainerRef, fileInputRef, lastStreamResultRef,
        handleSubmit, handleFileSelected, handleDeleteResult, handleDownloadPill, handleRerunStream,
        attachRenderedVideoForced, attachStreamRecordingUrlForced,
        handleSelectLiveStreams, handleSelectUserVideo, handleSelectDemoVideo, handleSignOut,
        planTier, userName, userInitials,
      }} />
    </V2Providers>
  );

  return (
    <V2Providers values={{ inference: inferenceContextValue, stream: streamContextValue, videoLibrary: videoLibraryContextValue }}>
      {anonInfoBanner}
      {anonTrialOverlay}
      {anonProvisioningFailedOverlay}
      <DesktopLayout {...{
        showTermsForAction, handleTermsAccepted, showConversionDialog, conversionFile,
        handleConversionConfirm, handleConversionCancel, showUpgradeForDownload, setShowUpgradeForDownload,
        sidebarOpen, setSidebarOpen, sidebarPanel, setSidebarPanel,
        beforeStateActive, afterState, setAfterState, carouselVisible, carouselDismissed, setCarouselDismissed,
        offlineJobActive, slotRevealed, anyPillApiOpen, setAnyPillApiOpen,
        prompt, setPrompt, videoMenuOpen, setVideoMenuOpen,
        compiledQuery, parseState, queryError,
        onCompiledQueryChange: setOverriddenQuery,
        onEditModeToggle: setEditMode,
        showProgress, showStreamProgress, showCarousel, authLoaded, streamProgressDismissing, offlineProgressDismissing,
        processingText, processingStep, jobProgress, completingSteps,
        recordingLimitBannerShown, setRecordingLimitBannerShown, isStreamingActive,
        streamErrorState, setStreamErrorState, quotaBar, costPreview, videoSource, canDownload, downloadingPillIds,
        publicVideoId: activeEntry?.publicVideoId ?? null,
        streamPillIdRef, uploadingPillIdsRef, autoRetryCountRef, streamWasLiveRef,
        flyDivRef, flyThumbnailRef, camRef, desktopThreadAreaRef, threadBottomRef,
        promptTextareaRef, fileInputRef, videoSlotRefCallback,
        planTier, userName, userInitials, lastStreamResultRef,
        handleSubmit, handleFileSelected, handleDeleteResult, handleDownloadPill, handleRerunStream,
        attachRenderedVideoForced, attachStreamRecordingUrlForced,
        handleSelectUserVideo, handleDeleteUserVideo, handleSelectDemoVideo,
        handleSelectLiveStreams, handleDeleteLiveStreamSessions, handleLiveStreamsNewSession,
        handleDeleteDemoVideoSession, handleSignOut, setSavedUserVideos,
      }} />
    </V2Providers>
  );
}

export default function PrimateVisionV2() {
  return <PrimateVisionV2Inner />;
}
